import { useEffect, useState } from "react";
import axios from "axios";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ShoppingBag, Search, Eye, Phone, Mail, Calendar, CheckCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    filterOrders();
  }, [searchTerm, orders]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
      setFilteredOrders(response.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterOrders = () => {
    if (!searchTerm) {
      setFilteredOrders(orders);
      return;
    }
    const filtered = orders.filter(order => 
      order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_phone.includes(searchTerm) ||
      (order.customer_email && order.customer_email.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredOrders(filtered);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-HT', {
      style: 'decimal',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-HT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    if (status === 'completed') {
        return (
            <span className="px-4 py-1.5 rounded-xl bg-green-500 text-white text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-green-100">
                <CheckCircle className="w-3 h-3" /> Konplete
            </span>
        );
    }
    return (
        <span className="px-4 py-1.5 rounded-xl bg-amber-500 text-white text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-amber-100">
            <Clock className="w-3 h-3" /> Atant
        </span>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
                <h1 className="text-4xl font-black text-[#232F3E] tracking-tighter italic uppercase leading-none">
                    Kòmand <span className="text-[#FF9900]">Kat Kado</span>
                </h1>
                <p className="text-gray-400 font-bold uppercase tracking-widest text-[9px] mt-2">
                    Jere tout kòmand kat kado Amazon kliyan yo achte.
                </p>
            </div>
            
            <div className="relative w-full md:w-96">
                <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                    placeholder="Chèche pa ID oswa telefòn..."
                    className="h-16 rounded-[25px] bg-white border-0 shadow-2xl pl-16 pr-8 font-black text-xs uppercase tracking-wider text-gray-900 focus:ring-0 focus:border-[#FF9900] transition-all"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
        </div>

        {loading ? (
            <div className="flex justify-center py-32">
                <div className="w-14 h-14 border-4 border-[#FF9900] border-t-transparent rounded-full animate-spin"></div>
            </div>
        ) : (
          <Card className="border-0 shadow-[0_50px_100px_-20px_rgba(255,153,0,0.12)] rounded-[45px] overflow-hidden bg-white">
            <CardContent className="p-0">
              {filteredOrders.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50/50 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] border-b border-gray-100/50">
                        <th className="py-8 px-10 text-left">Kat / Tranzaksyon</th>
                        <th className="py-8 px-8 text-left">Peman HTG</th>
                        <th className="py-8 px-8 text-left">Kliyan</th>
                        <th className="py-8 px-8 text-left">Estati</th>
                        <th className="py-8 px-8 text-left">Dat</th>
                        <th className="py-8 px-10 text-center">Aksyon</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {filteredOrders.map((order) => (
                        <tr key={order.id} className="hover:bg-gray-50/80 transition-all border-b border-gray-50 last:border-0 group">
                          <td className="py-8 px-10">
                            <div className="flex flex-col">
                                <span className="font-black text-gray-900 text-lg tracking-tight group-hover:text-[#FF9900] transition-colors line-clamp-1">${order.denomination} Gift Card</span>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className="text-[9px] text-gray-300 font-bold uppercase tracking-widest">ORDER: {order.id.slice(0, 8)}</span>
                                </div>
                            </div>
                          </td>
                          <td className="py-8 px-8">
                             <div className="flex flex-col">
                                <span className="text-xl font-black text-[#FF9900] italic tracking-tighter leading-none">{formatPrice(order.price_htg)} <span className="text-[10px] text-gray-400 not-italic ml-0.5">HTG</span></span>
                             </div>
                          </td>
                          <td className="py-8 px-8">
                             <div className="space-y-1.5">
                                <div className="flex items-center gap-2 text-gray-800 font-black text-xs">
                                    <Phone className="w-3.5 h-3.5 text-gray-300" /> {order.customer_phone}
                                </div>
                                {order.customer_email && (
                                    <div className="flex items-center gap-2 text-gray-400 font-bold text-[10px] overflow-hidden truncate max-w-[150px]">
                                        <Mail className="w-3 h-3 opacity-30" /> {order.customer_email}
                                    </div>
                                )}
                             </div>
                          </td>
                          <td className="py-8 px-8">
                            {getStatusBadge(order.payment_status)}
                          </td>
                          <td className="py-8 px-8 text-[11px] font-black text-gray-300 uppercase tracking-tighter italic">
                             <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 opacity-30" /> {formatDate(order.created_at)}
                             </div>
                          </td>
                          <td className="py-8 px-10 text-center">
                            <Button
                              variant="ghost"
                              className="w-12 h-12 p-0 rounded-2xl bg-gray-50 text-gray-400 hover:bg-[#232F3E] hover:text-white transition-all shadow-sm hover:scale-110 active:scale-95"
                              onClick={() => setSelectedOrder(order)}
                            >
                              <Eye className="w-5 h-5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-48 text-gray-400 flex flex-col items-center">
                  <ShoppingBag className="w-24 h-24 mb-8 opacity-5 italic" />
                  <p className="font-black uppercase tracking-widest text-[11px] leading-loose">Pa gen kòmand ki koresponn ak rechèch ou an.</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Order Detail Dialog */}
        <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
          <DialogContent className="max-w-md p-0 overflow-hidden border-0 rounded-[40px] shadow-2xl">
            <DialogHeader className="p-10 bg-[#232F3E] text-white">
              <DialogTitle className="text-3xl font-black uppercase tracking-tighter italic flex items-center gap-3">
                <ShoppingBag className="w-8 h-8 text-[#FF9900]" /> Detay <span className="text-[#FF9900]">Kòmand</span>
              </DialogTitle>
            </DialogHeader>
            {selectedOrder && (
              <div className="p-10 space-y-8 bg-white" data-testid="order-detail-modal">
                <div className="grid grid-cols-2 gap-8 text-[11px]">
                  <div className="space-y-1">
                    <p className="font-black uppercase tracking-widest text-gray-400">ID Kòmand</p>
                    <p className="font-black text-gray-900 border-b border-gray-50 pb-2">{selectedOrder.id}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="font-black uppercase tracking-widest text-gray-400">Estati</p>
                    <div className="pt-1">{getStatusBadge(selectedOrder.payment_status)}</div>
                  </div>
                  <div className="space-y-1">
                    <p className="font-black uppercase tracking-widest text-gray-400">ValèUSD</p>
                    <p className="text-xl font-black text-gray-900 underline decoration-[#FF9900] decoration-4 underline-offset-4 tracking-tighter">${selectedOrder.denomination}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="font-black uppercase tracking-widest text-gray-400">Pri HTG</p>
                    <p className="text-xl font-black text-[#FF9900] italic tracking-tighter">{formatPrice(selectedOrder.price_htg)} HTG</p>
                  </div>
                  <div className="space-y-1">
                    <p className="font-black uppercase tracking-widest text-gray-400">Fich Kat Kado</p>
                    <p className="text-gray-900 font-black">{selectedOrder.customer_phone}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="font-black uppercase tracking-widest text-gray-400">Imèl</p>
                    <p className="text-gray-900 font-black truncate max-w-full">{selectedOrder.customer_email || '-'}</p>
                  </div>
                  {selectedOrder.gift_card_code && (
                    <div className="col-span-2 p-6 rounded-[25px] bg-[#FF9900]/5 border border-[#FF9900]/10 flex flex-col items-center gap-2">
                      <p className="font-black uppercase tracking-widest text-gray-400 text-[10px]">🎁 Kòd Kat Kado</p>
                      <p className="text-3xl font-black text-[#FF9900] tracking-widest select-all">{selectedOrder.gift_card_code}</p>
                    </div>
                  )}
                  {selectedOrder.moncash_transaction_id && (
                    <div className="col-span-2 p-4 rounded-2xl bg-gray-50 border border-gray-100">
                      <p className="font-black uppercase tracking-widest text-gray-400 text-[9px] mb-1">Tranzaksyon Moncash</p>
                      <p className="font-mono text-[10px] text-gray-700 break-all">{selectedOrder.moncash_transaction_id}</p>
                    </div>
                  )}
                  <div className="col-span-2 pt-4 flex items-center gap-4 text-gray-300 font-black uppercase tracking-widest text-[9px]">
                    <Clock className="w-3 h-3" /> Kreye: {formatDate(selectedOrder.created_at)}
                  </div>
                </div>
                
                <Button 
                    className="w-full h-16 rounded-[25px] bg-[#232F3E] hover:bg-black text-white font-black text-lg shadow-2xl transition-all"
                    onClick={() => setSelectedOrder(null)}
                >
                    Fèmen Detay
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};
